'use client';

import { useEffect, useRef, useState } from 'react';
import { Github, Star, GitFork, Code2, ExternalLink, Zap, Users, AlertCircle } from 'lucide-react';
import SectionHeader from './SectionHeader';

interface GitHubRepo {
  name: string;
  description: string | null;
  language: string | null;
  stargazers_count: number;
  forks_count: number;
  watchers_count: number;
  updated_at: string;
  topics: string[];
  private: boolean;
  html_url: string;
}

interface GitHubUser {
  login: string;
  name: string;
  followers: number;
  following: number;
  public_repos: number;
  bio: string | null;
  avatar_url: string;
}

const LANG_COLORS: Record<string, string> = {
  Python: '#3572A5',
  JavaScript: '#f7df1e',
  TypeScript: '#3178c6',
  'C++': '#f34b7d',
  'C#': '#178600',
  Java: '#b07219',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Dart: '#00B4AB',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) > 1 ? 's' : ''} ago`;
}

export default function GitHubSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    async function fetchGitHub() {
      try {
        const [userRes, reposRes] = await Promise.all([
          fetch('https://api.github.com/users/Marwan-Hussein'),
          fetch('https://api.github.com/users/Marwan-Hussein/repos?sort=updated&per_page=6'),
        ]);

        if (!userRes.ok || !reposRes.ok) {
          throw new Error('Failed to fetch GitHub data');
        }

        const userData: GitHubUser = await userRes.json();
        const reposData: GitHubRepo[] = await reposRes.json();

        setUser(userData);
        setRepos(reposData.filter((r) => !r.private));
      } catch {
        setError('Could not load GitHub data. Please visit the profile directly.');
      } finally {
        setLoading(false);
      }
    }

    fetchGitHub();
  }, []);

  const totalStars = repos.reduce((sum, r) => sum + r.stargazers_count, 0);

  return (
    <section id="github" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader
            index="06 — Open Source"
            title="GitHub"
            highlight="activity"
            description={
              <>
                <a
                  href="https://github.com/Marwan-Hussein"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sand hover:underline"
                >
                  @Marwan-Hussein
                </a>{' '}
                on GitHub
              </>
            }
            align="center"
          />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-parchment-muted">
              <div className="w-5 h-5 border-2 border-sand/30 border-t-sand rounded-full animate-spin" />
              <span className="font-mono text-sm">Loading GitHub data…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12">
            <div className="warm-card rounded-xl p-6 flex items-center gap-3 text-parchment-muted border-rose/20">
              <AlertCircle size={18} className="text-rose flex-shrink-0" />
              <div>
                <p className="text-sm">{error}</p>
                <a
                  href="https://github.com/Marwan-Hussein"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sand text-xs font-mono hover:underline mt-1 inline-block"
                >
                  Visit GitHub Profile →
                </a>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && user && (
          <>
            <div
              className={`flex flex-wrap justify-center gap-6 xl:gap-10 mb-12 transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {[
                { label: 'Repos', value: user.public_repos, icon: Code2, color: '#c9a87c' },
                { label: 'Stars', value: totalStars, icon: Star, color: '#d4a574' },
                { label: 'Followers', value: user.followers, icon: Users, color: '#7d9b8a' },
                { label: 'Following', value: user.following, icon: Zap, color: '#a0785a' },
              ].map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="text-center px-6">
                    <Icon size={18} className="mx-auto mb-2" style={{ color: stat.color }} />
                    <div className="font-display text-3xl font-700 mb-0.5" style={{ color: stat.color }}>
                      {stat.value}
                    </div>
                    <div className="text-parchment-muted text-xs">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12 transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="warm-card rounded-xl p-5">
                <h4 className="font-display text-parchment text-sm font-600 mb-1">Contribution streak</h4>
                <p className="text-parchment-muted text-xs mb-4">Current streak and stats</p>
                <img
                  src="https://github-readme-streak-stats.herokuapp.com?user=Marwan-Hussein&theme=dark&hide_border=true&card_width=520&card_height=210"
                  alt="GitHub Streak Stats"
                  className="w-full h-auto rounded-lg opacity-90"
                  loading="lazy"
                />
              </div>
              <div className="warm-card rounded-xl p-5 lg:mt-8">
                <h4 className="font-display text-parchment text-sm font-600 mb-1">Top languages</h4>
                <p className="text-parchment-muted text-xs mb-4">Most used languages</p>
                <img
                  src="https://github-readme-stats.vercel.app/api/top-langs/?username=Marwan-Hussein&theme=gruvbox&hide_border=true&include_all_commits=false&count_private=false&layout=compact"
                  alt="Top Languages"
                  className="w-full h-auto rounded-lg opacity-90"
                  loading="lazy"
                />
              </div>
            </div>

            {repos.length > 0 && (
              <div
                className={`transition-all duration-700 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <h3 className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Github size={14} className="text-sand" />
                  Recent repositories
                </h3>

                <div className="space-y-4 max-w-3xl">
                  {repos.map((repo, i) => (
                    <a
                      key={repo.name}
                      href={repo.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`warm-card warm-card-hover rounded-xl p-5 block group transition-all duration-500 ${
                        i % 2 === 1 ? 'lg:ml-12' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Github size={14} className="text-parchment-muted flex-shrink-0" />
                          <span className="text-sand font-mono text-sm font-600 truncate group-hover:underline">
                            {repo.name}
                          </span>
                        </div>
                        <ExternalLink
                          size={12}
                          className="text-parchment-muted/40 group-hover:text-parchment-muted flex-shrink-0 ml-2 transition-colors"
                        />
                      </div>

                      <p className="text-parchment-muted text-sm leading-relaxed mb-4 line-clamp-2">
                        {repo.description || 'No description provided.'}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {repo.language && (
                            <div className="flex items-center gap-1.5">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: LANG_COLORS[repo.language] || '#a39e96' }}
                              />
                              <span className="text-parchment-muted text-xs">{repo.language}</span>
                            </div>
                          )}
                          <span className="flex items-center gap-1 text-parchment-muted text-xs">
                            <Star size={11} />
                            {repo.stargazers_count}
                          </span>
                          <span className="flex items-center gap-1 text-parchment-muted text-xs">
                            <GitFork size={11} />
                            {repo.forks_count}
                          </span>
                        </div>
                        <span className="font-mono text-[10px] text-parchment-muted/50">
                          {timeAgo(repo.updated_at)}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <a
                href="https://github.com/Marwan-Hussein"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
              >
                <Github size={16} />
                Full GitHub profile
                <ExternalLink size={14} />
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
